/* eslint-disable unicorn/no-array-reduce */
import { useMemo, useCallback } from 'react';
import { Spinner } from '@iq/react-components';
import { useSelector } from 'react-redux';
import {
  ResponsiveContainer,
  BarChart,
  YAxis,
  XAxis,
  Bar,
  ReferenceLine,
  Legend,
  Tooltip,
} from 'recharts';
import classnames from 'classnames';

import DegradedQualityIndicator from '../shared/DegradedQualityIndicator';
import { getSiteVariablesIndex } from '../../../../../bundles/sources';
import { useClientSize } from '../../../../../utils';
import colors, { resolveColor } from '../../../../../colors';
import { THRESHOLD_TYPES } from '../../../../../constants';
import { resolveThresholdItems } from '../../schemas/thresholdSchema';
import { format, resolveVariableUnit } from '../../../../../units';
import Loader from '../../../../Loader';
import { DropdownMenu } from '../../../../DropdownMenu';
import LabelWithTooltip from '../../../../LabelWithTooltip';

const getBarDataKey = (signalLabel) => (groupData) => {
  return groupData[signalLabel];
};

const tooltipFormatter = (value, name, props) => {
  const displayValue = props.payload?.[`${name}-display`];
  return [displayValue ?? value, name];
};

const CustomLegend = ({
  signalLabels,
  payload,
  isAreaMode,
  thresholdCount,
  sortedThresholds,
  isVertical,
}) => {
  const signalPayload = isAreaMode
    ? signalLabels.map((_, index) => payload[index * (1 + thresholdCount)])
    : payload?.slice(0, signalLabels.length);

  const size = 10;
  const linearHeight = 15;
  const linearWidth = 25;

  return (
    <ul className="graph-legend">
      {signalPayload?.map((dataSet, index) => {
        const key = `${dataSet?.color}-${index}`;
        return (
          <li
            key={key}
            className="graph-legend-item"
          >
            <svg
              width="10"
              height="10"
            >
              <rect
                width="10"
                height="10"
                fill={dataSet?.color}
              />
            </svg>
            {`${signalLabels[index]}`}
          </li>
        );
      })}
      {sortedThresholds?.map((threshold, index) =>
        threshold.description ? (
          <li
            key={`threshold-${threshold.max || threshold.value}-${index}`}
            className="graph-legend-item"
          >
            <svg
              width={!isAreaMode && !isVertical ? linearWidth : size}
              height={!isAreaMode ? linearHeight : size}
            >
              {!isAreaMode ? (
                <line
                  x1={isVertical ? size / 2 : 0}
                  x2={isVertical ? size / 2 : linearWidth}
                  y1={isVertical ? 0 : linearHeight / 2}
                  y2={isVertical ? linearHeight : linearHeight / 2}
                  strokeWidth={isVertical ? 5 : 3}
                  stroke={resolveColor(threshold.fill)}
                />
              ) : (
                <rect
                  x="0"
                  y="0"
                  width={size}
                  height={size}
                  fill={`${resolveColor(threshold.fill)}40`}
                />
              )}
            </svg>
            <span>{threshold.description}</span>
          </li>
        ) : null
      )}
    </ul>
  );
};

const GroupedBarVisualization = ({
  visualization,
  onFullscreen,
  isFullscreen,
  isPreview,
  getMoreOptions,
  dropdownPlacement,
  disconnected,
  latestValues: values,
  initialLoaded: initialLoadedOverride,
  loading: loadingOverride,
}) => {
  const variablesIndex = useSelector(getSiteVariablesIndex);

  const initialLoaded = initialLoadedOverride ?? Boolean(values);
  const loading = loadingOverride ?? !values;

  const { configuration = {}, variables = [] } = visualization;

  const { thresholds = {}, layout, groups = [] } = configuration;
  const thresholdType = thresholds.thresholdType || THRESHOLD_TYPES.linear.value;
  const isLinearThresholdType = thresholdType === THRESHOLD_TYPES.linear.value;
  const isVertical = layout === 'vertical';

  const [{ width, height }, clientReference] = useClientSize();

  const sortedThresholds = useMemo(
    () => resolveThresholdItems(thresholds, variables),
    [thresholds, variables]
  );

  const valueWithDecimals = useCallback(
    (rawValue) => {
      const extracted = Array.isArray(rawValue) ? rawValue.at(-2) : rawValue;
      if (extracted == null || Number.isNaN(Number(extracted))) {
        return null;
      }
      const { decimals } = configuration;
      return Number(extracted).toFixed(decimals);
    },
    [configuration]
  );

  const data = useMemo(() => {
    if (initialLoaded && values) {
      return (values || []).reduce((accumulator, { variable, value }) => {
        const variableConfig = variables.find((v) => v.id === variable);

        const { label, name } = variableConfig || {};

        const signalUnit = (variablesIndex || {})[variable]?.unit;

        // unit/unitLabel apply to the whole visualization, not per signal, like Metric/Gauge/Pie
        const { displayUnit, customUnit } = resolveVariableUnit(
          configuration.unit || 'variable-default',
          configuration.unitLabel,
          signalUnit
        );

        const rawValue =
          configuration.decimals || configuration.decimals === 0 ? valueWithDecimals(value) : value;

        const { value: formattedValue, unit: resolvedUnit } = format(
          displayUnit,
          rawValue,
          configuration.decimals,
          customUnit
        );

        const formattedValueWithUnit = `${formattedValue} ${resolvedUnit}`.trim();

        return [
          ...accumulator,
          {
            id: variable,
            name: label || name || variable,
            unit: resolvedUnit,
            value: rawValue,
            formattedValue,
            formattedValueWithUnit,
            quality: value && value.length >= 3 ? (value.at(-1) ?? 1) : 2,
          },
        ];
      }, []);
    }
    return [];
  }, [values, initialLoaded, variables, configuration, variablesIndex, valueWithDecimals]);

  const sortedData = useMemo(() => data?.toSorted((a, b) => a.value - b.value) || [], [data]);

  const isDataInvalid =
    data?.some((signalPoint) => signalPoint.quality > 1) ||
    (initialLoaded && variables.length > 0 && data?.length === 0) ||
    disconnected;

  const barMax = useMemo(
    () =>
      Math.max(
        sortedThresholds?.at(-1)?.[isLinearThresholdType ? 'value' : 'max'] || 0,
        sortedData?.at(-1)?.value || 0
      ),
    [sortedThresholds, sortedData, isLinearThresholdType]
  );

  const barMin = useMemo(
    () =>
      Math.min(
        sortedThresholds?.at(0)?.[isLinearThresholdType ? 'value' : 'max'] || 0,
        sortedData?.at(0)?.value || 0
      ),
    [sortedThresholds, sortedData, isLinearThresholdType]
  );

  const getPaddedValue = useCallback(
    (value) => {
      if (width < 500) {
        return (value * 100) / 75;
      }
      if (width < 1000) {
        return (value * 100) / 85;
      }
      return (value * 100) / 90;
    },
    [width]
  );

  const dataDomain = useMemo(() => {
    if (!isLinearThresholdType && sortedThresholds?.length) {
      return [sortedThresholds.at(0).min, sortedThresholds.at(-1).max];
    }
    const numberDigitsInMax = Math.floor(barMax).toString().length;
    let padX = 2;
    if (numberDigitsInMax > 1) {
      padX = 5 * 10 ** (numberDigitsInMax - 2);
    }
    const domainMax = getPaddedValue(barMax);
    const domainMin = barMin;
    // adjust to move up to the nearest sensible padX factor
    const adjustedDomainMax = Math.ceil(domainMax / padX) * padX;
    return [domainMin, adjustedDomainMax];
  }, [isLinearThresholdType, sortedThresholds, getPaddedValue, barMax, barMin]);

  const [signalLabels, groupData] = useMemo(() => {
    const signalLabels = new Set();
    const groupedData =
      data.length > 0
        ? groups.map((group) => {
            const { signals: groupSignals = [], groupLabel } = group;
            return groupSignals.reduce((accumulator, signal) => {
              const dataPoint = data.find((dataPoint) => dataPoint.id === signal.id);
              if (!accumulator.name) {
                accumulator.name = groupLabel;
              }
              const isValidDataPoint = dataPoint && dataPoint.quality === 1 && !disconnected;
              const safeSignalLabel = signal.signalLabel || signal.id;
              signalLabels.add(safeSignalLabel);
              const signalValue = isValidDataPoint ? dataPoint.value : 0;
              accumulator[safeSignalLabel] = signalValue;
              accumulator[`${safeSignalLabel}-display`] = dataPoint?.formattedValueWithUnit;

              if (!isLinearThresholdType && sortedThresholds?.length) {
                const sizedBars = [];
                sortedThresholds.forEach((threshold, index, array) => {
                  const thresholdMin = threshold.min || 0;
                  const thresholdMax = threshold.max || threshold.min || 0;
                  const previousThreshold = array[index - 1];
                  const thresholdSize = thresholdMax - thresholdMin;

                  if (index === 0) {
                    sizedBars.push({ ...threshold, size: thresholdSize });
                  } else if (thresholdMin < previousThreshold.max) {
                    if (thresholdMax > previousThreshold.max) {
                      sizedBars.push({
                        ...threshold,
                        min: previousThreshold.max,
                        size: thresholdMax - previousThreshold.max,
                      });
                    }
                  } else {
                    if (thresholdMin > previousThreshold.max) {
                      sizedBars.push({
                        min: previousThreshold.max,
                        max: thresholdMin,
                        size: thresholdMin - previousThreshold.max,
                      });
                    }
                    sizedBars.push({ ...threshold, size: thresholdSize });
                  }
                });

                const filteredBars = sizedBars.filter((b) => b.max >= signalValue);
                if (filteredBars.length > 0) {
                  filteredBars[0].size = filteredBars[0].max - signalValue;
                }

                filteredBars.forEach((th) => {
                  accumulator[`${safeSignalLabel}-th-${th.max}`] = th.size;
                });
              }

              return accumulator;
            }, {});
          })
        : [];
    return [[...signalLabels], groupedData];
  }, [data, disconnected, groups, isLinearThresholdType, sortedThresholds]);

  const vizHeight = height - 28; // less header
  const loaderHeight = vizHeight - 28; // less loader padding

  const classes = classnames('menu', {});

  const hasValidData = data?.some(
    (item) =>
      item?.value !== null && item?.value !== undefined && !Number.isNaN(Number(item?.value))
  );
  const isVizEmpty = !hasValidData && !loading && initialLoaded;
  const showOptionsMenu = !isPreview && !isVizEmpty;

  return (
    <div
      className={`grouped-bar-gauge-visualization-component ${isDataInvalid ? 'invalid-data' : ''}`}
      ref={clientReference}
    >
      {!isFullscreen && (
        <div className="grouped-bar-gauge-header">
          <div className="grouped-bar-gauge-title viz-wrapper">
            <span className="title viz-name">
              <LabelWithTooltip name={configuration?.title || visualization?.name || '\u00A0'} />
            </span>
            <span className="viz-help">{isDataInvalid ? <DegradedQualityIndicator /> : null}</span>
          </div>
          {!isPreview && (
            <div className={classes}>
              {initialLoaded && loading && (
                <Spinner
                  size="s"
                  className="spinner"
                />
              )}
              {showOptionsMenu && (
                <DropdownMenu
                  trigger="he-moreoptionsvertical"
                  triggerSize="s"
                  menuOptions={getMoreOptions({ loading, isPreview, data, onFullscreen })}
                  menuXPlacement={dropdownPlacement?.x}
                  menuYPlacement={dropdownPlacement?.y}
                  menuShouldBlockScroll
                  styles={{ dropdownIndicator: { padding: '0px' }, option: { cursor: 'pointer' } }}
                />
              )}
            </div>
          )}
        </div>
      )}
      {!initialLoaded && loading ? (
        <Loader
          text="Loading ..."
          height={loaderHeight}
        />
      ) : (
        <div className="grouped-bar-gauge-wrapper">
          <ResponsiveContainer
            width="100%"
            height={vizHeight}
          >
            <BarChart
              data={groupData}
              layout={configuration.layout}
              maxBarSize={width / 8}
            >
              <XAxis
                type={isVertical ? 'number' : 'category'}
                dataKey={isVertical ? null : 'name'}
                ticks={
                  isVertical
                    ? sortedThresholds?.map((th) => th.value)
                    : groupData.map((d) => d.name || null)
                }
                minTickGap={0}
                tick={{ fontSize: 11 }}
                domain={isVertical && dataDomain}
              />
              <YAxis
                type={isVertical ? 'category' : 'number'}
                dataKey={isVertical ? 'name' : null}
                ticks={
                  isVertical
                    ? groupData.map((d) => d.name || null)
                    : isLinearThresholdType
                      ? sortedThresholds?.map((th) => th.value)
                      : undefined
                }
                minTickGap={0}
                tick={{ fontSize: 11 }}
                domain={!isVertical && dataDomain}
                orientation="left"
              />
              {sortedThresholds?.map((threshold, index) => {
                if (isLinearThresholdType) {
                  return (
                    <ReferenceLine
                      key={`th-${index}-${threshold.value}-${threshold.fill}`}
                      y={isVertical ? undefined : threshold.value}
                      x={isVertical ? threshold.value : undefined}
                      stroke={resolveColor(threshold.fill)}
                      strokeWidth={2}
                    />
                  );
                }
                return null;
              })}
              {isLinearThresholdType
                ? signalLabels.map((signalLabel, index) => (
                    <Bar
                      key={`bar-${index}-${signalLabel}`}
                      name={signalLabel}
                      dataKey={getBarDataKey(signalLabel)}
                      fill={colors[index % colors.length].value}
                      isAnimationActive={false}
                    />
                  ))
                : signalLabels.flatMap((signalLabel, signalIndex) => [
                    <Bar
                      key={`bar-${signalIndex}-${signalLabel}`}
                      name={signalLabel}
                      stackId={`stack-${signalLabel}`}
                      dataKey={getBarDataKey(signalLabel)}
                      fill={colors[signalIndex % colors.length].value}
                      isAnimationActive={false}
                    />,
                    ...sortedThresholds.map((threshold, thIndex) => (
                      <Bar
                        key={`bar-${signalIndex}-${signalLabel}-th-${thIndex}-${threshold.max}`}
                        stackId={`stack-${signalLabel}`}
                        dataKey={(d) => d[`${signalLabel}-th-${threshold.max}`] || 0}
                        fill={threshold.fill ? `${resolveColor(threshold.fill)}40` : 'transparent'}
                        isAnimationActive={false}
                        tooltipType="none"
                      />
                    )),
                  ])}
              <Tooltip
                allowEscapeViewBox={{ x: false, y: true }}
                cursor={{ fill: 'transparent' }}
                wrapperStyle={{ zIndex: '1000' }}
                labelStyle={{ marginBottom: '0.4rem' }}
                formatter={tooltipFormatter}
              />
              <Legend
                content={
                  <CustomLegend
                    signalLabels={signalLabels}
                    isAreaMode={!isLinearThresholdType}
                    thresholdCount={sortedThresholds?.length || 0}
                    sortedThresholds={sortedThresholds}
                    isVertical={isVertical}
                  />
                }
                height={36}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default GroupedBarVisualization;
